import shaarpLogo from "@/assets/shaarp-logo.svg";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { type SlashCommandPromptGroup } from "@/hooks/useSlashCommand";
import { useTranslation } from "@/hooks/useTranslation";
import {
  getPromptSlug,
  getPromptStyle,
  getPromptTitle,
  getSupportedLang,
} from "@/lib/prompt-utils";
import { cn } from "@/lib/utils";
import { PromptDTO } from "@shaarp/common";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

type SlashCommandAutocompleteProps = {
  groups: SlashCommandPromptGroup[];
  totalCount: number;
  onSelect: (prompt: PromptDTO) => void;
  onDismiss: () => void;
};

export type SlashCommandAutocompleteRef = {
  handleKeyDown: (e: React.KeyboardEvent) => void;
};

const SlashCommandAutocomplete = forwardRef<
  SlashCommandAutocompleteRef,
  SlashCommandAutocompleteProps
>(function SlashCommandAutocomplete(
  { groups, totalCount, onSelect, onDismiss },
  ref,
) {
  const { t, i18n } = useTranslation();
  const lang = getSupportedLang(i18n.language);
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const itemRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // Flat list of all prompts for keyboard navigation
  const flatPrompts = useMemo(
    () => groups.flatMap((g) => g.prompts),
    [groups],
  );

  // Reset selection when groups change
  useEffect(() => {
    setSelectedIndex(0);
  }, [groups]);

  // Scroll selected item into view
  useEffect(() => {
    const el = itemRefs.current.get(selectedIndex);
    el?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  useImperativeHandle(ref, () => ({
    handleKeyDown(e: React.KeyboardEvent) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < totalCount - 1 ? prev + 1 : 0,
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev > 0 ? prev - 1 : totalCount - 1,
        );
      } else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        if (flatPrompts[selectedIndex]) {
          onSelect(flatPrompts[selectedIndex]);
        }
      }
    },
  }));

  // Dismiss on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        onDismiss();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onDismiss]);

  let flatIndex = 0;

  return (
    <div
      ref={containerRef}
      className="absolute bottom-full left-0 right-0 z-50 mb-2 animate-in fade-in slide-in-from-bottom-2 duration-150"
    >
      <div className="rounded-lg border bg-popover shadow-lg text-left overflow-hidden">
        <div className="max-h-[260px] overflow-y-auto p-1">
          {totalCount === 0 ? (
            <div className="py-4 text-center text-sm text-muted-foreground">
              {t("chat.input.slashCommand.noResults")}
            </div>
          ) : (
            groups.map((group) => (
              <div key={group.key}>
                {groups.length > 1 && (
                  <div className="px-2.5 pt-2 pb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    {t(group.labelKey)}
                  </div>
                )}
                {group.prompts.map((prompt) => {
                  const currentIndex = flatIndex++;
                  const { Icon, colors } = getPromptStyle(prompt);
                  const title = getPromptTitle(prompt, lang);
                  const slug = getPromptSlug(prompt, lang);
                  const isSelected = currentIndex === selectedIndex;
                  const isPublicPrompt = prompt.ownerType === "public";
                  const lastUpdatedUser = !isPublicPrompt
                    ? (prompt.lastUpdatedBy ?? prompt.author ?? null)
                    : null;

                  return (
                    <div
                      key={prompt.id}
                      ref={(el) => {
                        if (el) itemRefs.current.set(currentIndex, el);
                        else itemRefs.current.delete(currentIndex);
                      }}
                      role="option"
                      aria-selected={isSelected}
                      className={cn(
                        "flex items-center gap-2.5 rounded-sm px-2.5 py-2 text-sm cursor-pointer select-none",
                        isSelected
                          ? "bg-accent text-accent-foreground"
                          : "hover:bg-accent/50",
                      )}
                      onClick={() => onSelect(prompt)}
                      onMouseEnter={() => setSelectedIndex(currentIndex)}
                    >
                      <div className="relative shrink-0">
                        <div
                          className={cn(
                            "flex items-center justify-center rounded-full size-7",
                            colors.iconBg,
                          )}
                        >
                          <Icon className={cn("size-3.5", colors.icon)} />
                        </div>
                        {(isPublicPrompt || lastUpdatedUser) && (
                          <div className="absolute -bottom-0.5 -right-0.5 ring-2 ring-popover rounded-full">
                            {isPublicPrompt ? (
                              <div className="flex items-center justify-center size-3.5 overflow-hidden bg-white rounded-full">
                                <img
                                  src={shaarpLogo}
                                  alt="Shaarp"
                                  className="object-contain size-3.5"
                                />
                              </div>
                            ) : (
                              <Avatar className="size-3.5">
                                <AvatarImage
                                  src={lastUpdatedUser?.imageUrl}
                                />
                                <AvatarFallback className="text-[6px] bg-muted">
                                  {(lastUpdatedUser?.firstName?.[0] ?? "") +
                                    (lastUpdatedUser?.lastName?.[0] ?? "")}
                                </AvatarFallback>
                              </Avatar>
                            )}
                          </div>
                        )}
                      </div>
                      <span className="flex-1 truncate">{title}</span>
                      {slug && (
                        <span className="shrink-0 rounded px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground bg-muted">
                          /{slug}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
});

export default SlashCommandAutocomplete;
